import {
  Box,
  Chip,
  Link,
  Stack,
  Button,
  Tooltip,
  TableRow,
  TableCell,
  IconButton,
  Typography,
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import {
  IInboundEmailLog,
  InboundEmailLogStatus,
} from 'src/services/inbound-email/inbound-email.types';

import Iconify from 'src/components/iconify';

type Props = {
  row: IInboundEmailLog;
  onView: (row: IInboundEmailLog) => void;
  onAssign: (row: IInboundEmailLog) => void;
};

const STATUS_COLOR: Record<InboundEmailLogStatus, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  [InboundEmailLogStatus.RECEIVED]: 'info',
  [InboundEmailLogStatus.PARSING]: 'info',
  [InboundEmailLogStatus.MATCHED]: 'success',
  [InboundEmailLogStatus.MATCHED_BY_DOMAIN_FALLBACK]: 'warning',
  [InboundEmailLogStatus.CANDIDATE_IMPORTED]: 'success',
  [InboundEmailLogStatus.UNMATCHED_NO_ORG]: 'error',
  [InboundEmailLogStatus.UNMATCHED_NO_JOB]: 'warning',
  [InboundEmailLogStatus.PARSE_FAILED]: 'error',
  [InboundEmailLogStatus.DUPLICATE]: 'default',
  [InboundEmailLogStatus.REJECTED_SPOOF_RISK]: 'error',
};

export default function InboundEmailTableRow({ row, onView, onAssign }: Props) {
  const sourceBoard = row.parsed_data?.source_board as string | undefined;
  const isAssignable =
    row.status === InboundEmailLogStatus.UNMATCHED_NO_JOB ||
    row.status === InboundEmailLogStatus.MATCHED_BY_DOMAIN_FALLBACK ||
    row.status === InboundEmailLogStatus.CANDIDATE_IMPORTED;

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2">
          {new Date(row.created_at).toLocaleString()}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {row.from_email}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip title={row.subject ?? ''}>
          <Typography
            variant="body2"
            sx={{
              maxWidth: 280,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.subject || <Box component="span" sx={{ color: 'text.disabled' }}>(no subject)</Box>}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Chip size="small" color={STATUS_COLOR[row.status] ?? 'default'} label={row.status.replace(/_/g, ' ')} />
      </TableCell>
      <TableCell>
        {sourceBoard ? (
          <Chip size="small" variant="outlined" label={sourceBoard} sx={{ textTransform: 'capitalize' }} />
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        )}
      </TableCell>
      <TableCell>
        {row.linked_candidate_id ? (
          <Link
            component={RouterLink}
            href={paths.dashboard.candidates.detail(row.linked_candidate_id)}
            variant="body2"
          >
            View candidate
          </Link>
        ) : (
          <Typography variant="caption" color="text.disabled">—</Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="View parsed data">
            <IconButton size="small" onClick={() => onView(row)}>
              <Iconify icon="mdi:code-json" width={20} />
            </IconButton>
          </Tooltip>
          {isAssignable && (
            <Button size="small" variant="outlined" onClick={() => onAssign(row)}>
              Assign to job
            </Button>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
